import ellipsisToWidth from './ellipsisToWidth';
import { Style, getLineHeight } from 'utils/types/Style';
import measureText from './measureText';

export interface WrappedValueLine {
    value: string;
    width: number;
}

export interface WrappedValue {
    lines: WrappedValueLine[];
    width: number;
    isTruncated: boolean;
}

export interface WrapTextOptions {
    style: Partial<Style>;
    groupHeight: number;
    availableLineWidth: number;
    context?: CanvasRenderingContext2D;
}

/*
 * Manual word wrap on a sentance with an ellipsis on the last line if required
 */
export default function wrapText(
    value: string,
    options: WrapTextOptions
): WrappedValue {
    const { style, groupHeight, availableLineWidth, context } = options;

    const width = measureText(value, style, context);

    if (width <= availableLineWidth) {
        return { width, isTruncated: false, lines: [{ width, value }] };
    }

    const lineHeight = getLineHeight(style);
    const lineCount = Math.max(1, Math.round(groupHeight / lineHeight));

    const words = value.split(' ');

    const ret: WrappedValue = {
        width: 0,
        lines: [{ value: '', width: 0 }],
        isTruncated: false,
    };

    function updateLine(line: WrappedValueLine, value: string) {
        line.value = value;
        line.width = measureText(value, style, context);
    }

    function appendAllRemaining(line: WrappedValueLine, word = '') {
        const text =
            line.value +
            ' ' +
            word +
            ' ' +
            words.splice(0, words.length).join(' ');
        updateLine(line, text);
    }

    while (words.length > 0) {
        let line = ret.lines[ret.lines.length - 1];

        const word = words.splice(0, 1)[0];
        const next = (line.value + ' ' + word).trim();
        const width = measureText(next, style, context);

        if (width <= availableLineWidth) {
            updateLine(line, next);
        } else {
            if (ret.lines.length === lineCount) {
                // Reached max lines - append remainder to last line
                appendAllRemaining(line, word);
            } else {
                // Start a new line with the word...
                line = {
                    width: measureText(word, style, context),
                    value: word,
                };
                ret.lines.push(line);

                if (line.width > availableLineWidth) {
                    // Word is too long. Make this the last line
                    appendAllRemaining(line);
                }
            }
        }
    }

    const lastLine = ret.lines[ret.lines.length - 1];
    if (lastLine.width > availableLineWidth) {
        lastLine.width = availableLineWidth;
        lastLine.value = ellipsisToWidth(
            lastLine.value,
            availableLineWidth,
            style,
            context
        );
        ret.isTruncated = true;
    }

    ret.width = Math.max(...ret.lines.map((l) => l.width));

    return ret;
}
